import {
  Editor,
  TLAssetId,
  TLImageAsset,
  TLImageShape,
  TLShapeId,
  TLVideoAsset,
  TLVideoShape,
  debounce,
  react,
  useDelaySvgExport,
  useEditor,
  useSvgExportContext,
} from "@tldraw/editor";
import { useEffect, useMemo, useRef, useState } from "react";
import { CustomImageUtilProps } from "../CustomImageUtil";
import { readS3Marker } from "pages/flyer/builder/utils/s3Marker";

/**
 * This is a handy helper hook that resolves an asset to an optimized URL for a given shape, or its
 * {@link @tldraw/editor#Editor.createTemporaryAssetPreview | placeholder} if the asset is still
 * uploading. This is used in particular for high-resolution images when you want lower and higher
 * resolution depending on the context.
 *
 * For image scaling to work, you need to implement scaled URLs in
 * {@link @tldraw/tlschema#TLAssetStore.resolve}.
 *
 */
export function useImageOrVideoAsset({
  shapeId,
  assetId,
  editor,
}: {
  shapeId: TLShapeId;
  assetId: TLAssetId | null;
  editor: Editor;
}) {
  //   const editor = useEditor();
  const isExport = !!useSvgExportContext();
  const isReady = useDelaySvgExport();

  const resolveAssetUrlDebounced = useMemo(() => debounce(resolveAssetUrl, 50), []);

  // We use a state to store the result of the asset resolution, and we're going to avoid updating this whenever we can
  const [result, setResult] = useState<{
    asset: (TLImageAsset | TLVideoAsset) | null;
    url: string | null;
  }>(() => ({
    asset: assetId && editor ? (editor.getAsset(assetId) as TLImageAsset | TLVideoAsset) ?? null : null,
    url: null as string | null,
  }));

  // A flag for whether we've resolved the asset URL at least once, after which we can debounce
  const didAlreadyResolve = useRef(false);

  // The last URL that we've seen for the shape
  const previousUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!assetId) return;

    let isCancelled = false;
    let cancelDebounceFn: (() => void) | undefined;

    const cleanupEffectScheduler = react("update state", () => {
      if (!isExport && editor && editor.getCulledShapes().has(shapeId)) return;

      // Get the fresh asset
      const asset = editor && (editor.getAsset(assetId) as TLImageAsset | TLVideoAsset);
      if (!asset) return;

      // Get the fresh shape
      const shape = editor.getShape<CustomImageUtilProps | TLVideoShape>(shapeId);
      if (!shape) return;

      // Set initial preview for the shape if it has no source (if it was pasted into a local project as base64)
      if (!asset.props.src) {
        const preview = editor.getTemporaryAssetPreview(asset.id);
        if (preview) {
          if (previousUrl.current !== preview) {
            previousUrl.current = preview; // just for kicks, let's save the url as the previous URL
            setResult((prev) => ({ ...prev, isPlaceholder: true, url: preview })); // set the preview as the URL
            isReady(); // let the SVG export know we're ready for export
          }
          return;
        }
      }

      // aside ...we could bail here if the only thing that has changed is the shape has changed from culled to not culled

      const screenScale = editor.getZoomLevel() * (shape.props.w / (asset.props as any).w);

      function resolve(asset: TLImageAsset | TLVideoAsset, url: string | null) {
        if (isCancelled) return; // don't update if the hook has remounted
        if (previousUrl.current === url) return; // don't update the state if the url is the same
        didAlreadyResolve.current = true; // mark that we've resolved our first image
        previousUrl.current = url; // keep the url around to compare with the next one
        setResult({ asset, url });
        isReady(); // let the SVG export know we're ready for export
      }

      // If we already resolved the URL, debounce fetching potentially multiple image variations.
      if (didAlreadyResolve.current) {
        resolveAssetUrlDebounced(editor, assetId, screenScale, isExport, (url) => resolve(asset, url));
        cancelDebounceFn = resolveAssetUrlDebounced.cancel; // cancel the debounce when the hook unmounts
      } else {
        resolveAssetUrl(editor, assetId, screenScale, isExport, (url) => resolve(asset, url));
      }
    });

    return () => {
      cleanupEffectScheduler();
      cancelDebounceFn?.();
      isCancelled = true;
    };
  }, [editor, assetId, isExport, isReady, shapeId, resolveAssetUrlDebounced]);

  return result;
}

function resolveAssetUrl(
  editor: Editor,
  assetId: TLAssetId,
  screenScale: number,
  isExport: boolean,
  callback: (url: string | null) => void
) {
  editor
    .resolveAssetUrl(assetId, {
      screenScale,
      shouldResolveToOriginal: isExport,
    })
    // There's a weird bug with out debounce function that doesn't
    // make it work right with async functions, so we use a callback
    // here instead of returning a promise.
    .then(async (url) => {
      // Check if the URL contains our S3 key marker for re-signing expired URLs
      if (url?.includes("#s3key=") || url?.includes("#s3bucket=")) {
        try {
          const { getSignedUrl } = await import("graphQL/query/file");

          const marker = readS3Marker(url);
          if (!marker) {
            callback(url);
            return;
          }

          const signedUrl = await getSignedUrl(marker.bucket, marker.key, "GET", marker.region);
          callback(signedUrl);
          return;
        } catch (error) {
          console.error("❌ [resolveAssetUrl] Error generating fresh signed URL:", error);
          const cleanUrl = url.split("#")[0];
          callback(cleanUrl);
          return;
        }
      }

      // Fallback: re-sign expired S3 URLs that don't have the #s3key= marker (legacy designs)
      if (url) {
        const s3Match = url.match(/^https?:\/\/([^/]+)\.s3(?:\.([^/.]+))?\.amazonaws\.com\/([^?]+)/);
        if (s3Match) {
          try {
            const { getSignedUrl } = await import("graphQL/query/file");
            const bucket = s3Match[1];
            const region = s3Match[2] || "ap-southeast-2";
            const key = decodeURIComponent(s3Match[3]);
            const signedUrl = await getSignedUrl(bucket, key, "GET", region);
            callback(signedUrl);
            return;
          } catch (error) {
            // Use original URL as fallback
          }
        }
      }

      callback(url);
    })
    .catch((error) => {
      console.error("❌ [resolveAssetUrl] Error resolving asset URL:", {
        assetId,
        error,
      });
      callback(null);
    });
}

/**
 * @deprecated Use {@link useImageOrVideoAsset} instead.
 *
 */
export const useAsset = useImageOrVideoAsset;
